import type { WebappResponse } from '@eventbrite/event-listing-service-client';

import type { BasicInfo } from '@/types';
import { shouldIndexEvent } from '@/utils/utils';

function getCleanListingUrl(url?: string): string | undefined {
  if (!url || url === '#') {
    return undefined;
  }

  return url;
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

export function isSeriesChildListing(basicInfo: BasicInfo): boolean {
  return Boolean(basicInfo.isSeries && !basicInfo.isParentEvent);
}

export function getParentListingUrlFromSeriesChild(
  childUrl: string,
  childEventId: string,
  seriesId: string,
): string | undefined {
  const cleanChildUrl = getCleanListingUrl(childUrl);

  if (!cleanChildUrl || !childEventId || !seriesId) {
    return undefined;
  }

  try {
    const parsedUrl = new URL(cleanChildUrl);
    const slug = parsedUrl.pathname.split('/').pop() ?? '';

    if (!slug.endsWith(childEventId)) {
      return undefined;
    }

    const childEventIdPattern = new RegExp(`${escapeRegExp(childEventId)}$`);
    parsedUrl.pathname = parsedUrl.pathname.replace(childEventIdPattern, seriesId);
    return parsedUrl.toString();
  } catch {
    return undefined;
  }
}

export function resolveListingCanonicalUrl(basicInfo: BasicInfo): string | undefined {
  const listingUrl = getCleanListingUrl(basicInfo.url);

  if (!listingUrl) {
    return undefined;
  }

  if (!isSeriesChildListing(basicInfo)) {
    return listingUrl;
  }

  if (!basicInfo.seriesId || !basicInfo.id) {
    return undefined;
  }

  return getParentListingUrlFromSeriesChild(listingUrl, basicInfo.id, basicInfo.seriesId);
}

export function resolveListingRobotsContent({
  basicInfo,
  isPreview,
  ebHost,
  seo,
}: {
  basicInfo: BasicInfo;
  isPreview?: boolean;
  ebHost?: string;
  seo?: WebappResponse['seo'];
}): string {
  if (isPreview) {
    return 'noindex, nofollow';
  }

  if (isSeriesChildListing(basicInfo)) {
    return 'noindex, follow';
  }

  const isIndexable = shouldIndexEvent(basicInfo.url, ebHost, seo?.isIndexable);
  return isIndexable ? 'index, follow' : 'noindex, nofollow';
}
